'use client';

import { c as _c } from "react/compiler-runtime";
import type { Post } from 'content-collections';
import NumberFlow from '@number-flow/react';
import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { BlurImage } from '@/components/blur-image';
import { ImageZoom } from '@/components/image-zoom';
import { Link } from '@/components/ui/link';
import { useCountComment } from '@/hooks/queries/comment.query';
import { useCountView, useIncrementView } from '@/hooks/queries/view.query';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { MY_NAME } from '@/lib/constants';
type BlogHeaderProps = {
  post: Post;
};
function BlogHeader(props) {
  const $ = _c(76);
  const {
    post
  } = props;
  const formattedDate = useFormattedDate(post.date);
  const t = useTranslations();
  let t0;
  if ($[0] !== post.slug) {
    t0 = {
      slug: post.slug
    };
    $[0] = post.slug;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const viewCountQuery = useCountView(t0);
  let t1;
  if ($[2] !== post.slug) {
    t1 = {
      slug: post.slug,
      withReplies: true
    };
    $[2] = post.slug;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const commentCountQuery = useCountComment(t1);
  let t2;
  if ($[4] !== post.slug) {
    t2 = {
      slug: post.slug
    };
    $[4] = post.slug;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    mutate: incrementPostView
  } = useIncrementView(t2);
  const incremented = useRef(false);
  let t3;
  let t4;
  if ($[6] !== incrementPostView || $[7] !== post.slug) {
    t3 = () => {
      if (!incremented.current) {
        incrementPostView({
          slug: post.slug
        });
        incremented.current = true;
      }
    };
    t4 = [incrementPostView, post.slug];
    $[6] = incrementPostView;
    $[7] = post.slug;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== post.title) {
    t5 = <h1 className="bg-linear-to-b from-black via-black/90 to-black/70 to-90% bg-clip-text text-center text-4xl font-semibold text-transparent md:text-5xl/16 dark:from-white dark:via-white/90 dark:to-white/70">{post.title}</h1>;
    $[10] = post.title;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("blog.header.written-by");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <div className="text-muted-foreground">{t6}</div>;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t) {
    t8 = t("common.avatar-alt", {
      name: MY_NAME
    });
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <Link href="https://github.com/braviaprime" className="flex items-center gap-2"><BlurImage src="/images/avatar.png" className="size-6 rounded-full" width={1024} height={1024} alt={t8} />{MY_NAME}</Link>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t7 || $[21] !== t9) {
    t10 = <div className="space-y-1 md:mx-auto">{t7}{t9}</div>;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t) {
    t11 = t("blog.header.published-on");
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11) {
    t12 = <div className="text-muted-foreground">{t11}</div>;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = formattedDate ?? "--";
  let t14;
  if ($[27] !== t13) {
    t14 = <div>{t13}</div>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t12 || $[30] !== t14) {
    t15 = <div className="space-y-1 md:mx-auto">{t12}{t14}</div>;
    $[29] = t12;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("blog.header.views");
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <div className="text-muted-foreground">{t16}</div>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const t18 = viewCountQuery.isLoading && "--";
  let t19;
  if ($[36] !== t || $[37] !== viewCountQuery.isError) {
    t19 = viewCountQuery.isError && t("common.error");
    $[36] = t;
    $[37] = viewCountQuery.isError;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== viewCountQuery.data || $[40] !== viewCountQuery.isSuccess) {
    t20 = viewCountQuery.isSuccess && <NumberFlow value={viewCountQuery.data.views} data-testid="view-count" />;
    $[39] = viewCountQuery.data;
    $[40] = viewCountQuery.isSuccess;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = <div className="space-y-1 md:mx-auto">{t17}{t18}{t19}{t20}</div>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t) {
    t22 = t("common.labels.comments");
    $[47] = t;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t22) {
    t23 = <div className="text-muted-foreground">{t22}</div>;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  const t24 = commentCountQuery.isLoading && "--";
  let t25;
  if ($[51] !== commentCountQuery.isError || $[52] !== t) {
    t25 = commentCountQuery.isError && t("common.error");
    $[51] = commentCountQuery.isError;
    $[52] = t;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== commentCountQuery.data || $[55] !== commentCountQuery.isSuccess) {
    t26 = commentCountQuery.isSuccess && <NumberFlow value={commentCountQuery.data.count} data-testid="comment-count" />;
    $[54] = commentCountQuery.data;
    $[55] = commentCountQuery.isSuccess;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t23 || $[58] !== t24 || $[59] !== t25 || $[60] !== t26) {
    t27 = <div className="space-y-1 md:mx-auto">{t23}{t24}{t25}{t26}</div>;
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t10 || $[63] !== t15 || $[64] !== t21 || $[65] !== t27) {
    t28 = <div className="grid grid-cols-2 text-sm max-md:gap-4 md:grid-cols-4">{t10}{t15}{t21}{t27}</div>;
    $[62] = t10;
    $[63] = t15;
    $[64] = t21;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t28 || $[68] !== t5) {
    t29 = <div className="space-y-16 sm:px-8">{t5}{t28}</div>;
    $[67] = t28;
    $[68] = t5;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  const t30 = `/images/blog/${post.slug}/cover.png`;
  let t31;
  if ($[70] !== post.title || $[71] !== t30) {
    t31 = <ImageZoom><BlurImage src={t30} className="rounded-lg" width={1200} height={630} lazy={false} fetchPriority="high" alt={post.title} /></ImageZoom>;
    $[70] = post.title;
    $[71] = t30;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== t29 || $[74] !== t31) {
    t32 = <div className="space-y-16 py-16">{t29}{t31}</div>;
    $[73] = t29;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  return t32;
}
export default BlogHeader;